import type {
  CellData,
  GridId,
  GridLayout,
  GridMap,
  PositionData,
  ScreenLayout,
} from "@observe-tech/utils";
import { isGridId, isGridIdInside, isGridLayout } from "@observe-tech/utils";
import type { SyncStore as Store } from "../../infra/state";
import { isVideoScreen } from "./screenType";

export const createValidationMethods = (store: Store) => {
  const isValidGridId = (gridId: GridId): boolean => isGridId(gridId);

  const isValidGridLayout = (gridLayout: GridLayout): boolean =>
    isGridLayout(gridLayout);

  const validateOverlays = (
    layout: ScreenLayout,
    gridToUse: GridMap,
  ): ScreenLayout => {
    const validateOverlay = (overlayId: GridId | null): GridId | null => {
      if (overlayId === null || !isValidGridId(overlayId)) return null;
      if (!isGridIdInside(overlayId, layout.screenData.gridLayout)) return null;

      const overlayData = gridToUse[overlayId];
      return overlayData ? overlayId : null;
    };

    layout.screenData.fullScreen = validateOverlay(
      layout.screenData.fullScreen,
    );
    layout.screenData.halfScreenLeft = validateOverlay(
      layout.screenData.halfScreenLeft,
    );
    layout.screenData.halfScreenRight = validateOverlay(
      layout.screenData.halfScreenRight,
    );

    return layout;
  };

  const validateLayout = (
    layout: ScreenLayout,
    gridToUse: GridMap,
  ): ScreenLayout => {
    if (!isVideoScreen(layout.screenType)) {
      return layout;
    }
    return validateOverlays(layout, gridToUse);
  };

  const validateCagePositionData = (
    data: PositionData,
  ): CellData<[number, ...number[]]> => {
    const isCagePositionData =
      "machineId" in data && "cageId" in data && !("cameraId" in data);
    if (!isCagePositionData) {
      throw new Error(
        "Source position data must include machineId and cageId for cage grid",
      );
    }

    // Prefer the loaded catalog when it knows this cage, but never reject the
    // write when it doesn't: the catalog mirror can lag behind the site list
    // the menu was built from (e.g. a machine whose data has not synced yet).
    // A subsequent loadMachines re-syncs the cameras for this cell.
    const machinesLoaded = store.get("machinesLoaded") || {};
    const cageInStore = machinesLoaded?.[data.machineId]?.find(
      (cage: { cageId: number; cameras: number[] }) =>
        cage.cageId === data.cageId,
    );
    if (cageInStore) {
      return {
        machineId: data.machineId,
        ...cageInStore,
      } as CellData<[number, ...number[]]>;
    }

    const providedCameras =
      "cameras" in data &&
      Array.isArray(data.cameras) &&
      data.cameras.length > 0
        ? (data.cameras as [number, ...number[]])
        : ([1] as [number, ...number[]]);

    return {
      machineId: data.machineId,
      cageId: data.cageId,
      cameras: providedCameras,
    };
  };

  const validateCameraPositionData = (
    data: PositionData,
  ): CellData<[number]> => {
    const isCameraPositionData =
      "machineId" in data && "cageId" in data && "cameraId" in data;
    if (!isCameraPositionData) {
      throw new Error(
        "Source position data must include machineId, cageId, and cameraId for camera grid",
      );
    }

    // Never reject the write when the catalog mirror does not yet know this
    // camera; the menu is built from the live site list, which can be ahead of
    // the loaded catalog for a just-reachable machine.
    return {
      machineId: data.machineId,
      cageId: data.cageId,
      cameras: [data.cameraId],
    };
  };

  return {
    isValidGridId,
    isValidGridLayout,
    validateOverlays,
    validateLayout,
    validateCagePositionData,
    validateCameraPositionData,
  };
};
